import _ from 'lodash'
import { Moment } from 'moment'
import React from 'react'
import { Link } from 'react-router'

import {
  FREQUENCIES,
  FREQUENCY_LABELS,
  MEDICATION_FORM_STATUSES,
  MEDICATION_FORM_STATUS_LABELS,
} from 'services/medicationForms/constants'
import { MedicationForm } from 'services/medicationForms/models'

import { generateRoute } from 'utils/routing'
import { createTypeFromEnumValues } from 'utils/typescript'

import { Avatar, DateString, DropdownMenu, Typography } from 'components'
import { TYPES } from 'components/DropdownMenu/constants'

import i18n from 'translations'

const getFrequency = (frequency: createTypeFromEnumValues<FREQUENCIES>, dose: string) => {
  if (frequency === FREQUENCIES.DAILY) {
    return (
      <React.Fragment>
        {FREQUENCY_LABELS[frequency]}
        <br />
        <Typography width="80px" ellipsis>{dose}</Typography>
      </React.Fragment>
    )
  }

  return FREQUENCY_LABELS[frequency]
}

interface getRouteProps {
  childId: number
  id: number
  status: createTypeFromEnumValues<MEDICATION_FORM_STATUSES>
}

const getRoute = ({ childId, id, status }: getRouteProps) => {
  if (status === MEDICATION_FORM_STATUSES.DRAFT) {
    return childId
      ? generateRoute('CHILDREN.CHILD.SAFEGUARDING.MEDICATION_FORM.EDIT', { childId, id })
      : generateRoute('SAFEGUARDING.MEDICATION_FORM.EDIT', { id })
  }

  return childId
    ? generateRoute('CHILDREN.CHILD.SAFEGUARDING.MEDICATION_FORM.PREVIEW', { childId, id })
    : generateRoute('SAFEGUARDING.MEDICATION_FORM.PREVIEW', { id })
}

export interface MedicationFormTableData {
  actions: React.JSX.Element
  child: React.JSX.Element
  expiryDate: React.JSX.Element
  expiryDateValue: Date | Moment | string | number
  frequency: React.ReactNode
  id: number
  medication: React.JSX.Element
  reviewDate: React.JSX.Element
  startEndDate: React.JSX.Element
  status: typeof MEDICATION_FORM_STATUS_LABELS[keyof typeof MEDICATION_FORM_STATUS_LABELS]
  statusValue: createTypeFromEnumValues<MEDICATION_FORM_STATUSES>
}

export const getTableData = (
  medicationFormsList: MedicationForm[],
  childId: number,
  onExportClick: (id: number) => void,
): MedicationFormTableData[] | null => {
  if (!medicationFormsList?.length) {
    return null
  }

  return _.map(medicationFormsList, ({
    child,
    endDate,
    id,
    medicationDetails,
    nextReviewDate,
    startDate,
    status,
  }) => {
    const {
      dose,
      expiryDate,
      frequency,
      medication,
    } = medicationDetails || {}

    const reviewFormRoute = childId
      ? `${generateRoute('CHILDREN.CHILD.SAFEGUARDING.MEDICATION_FORM.PREVIEW', { childId, id })}?status=review`
      : `${generateRoute('SAFEGUARDING.MEDICATION_FORM.PREVIEW', { id })}?status=review`

    return {
      actions: (
        <DropdownMenu minWidth={290} small>
          {status === MEDICATION_FORM_STATUSES.DRAFT && (
            <DropdownMenu.Item
              to={childId
                ? generateRoute('CHILDREN.CHILD.SAFEGUARDING.MEDICATION_FORM.EDIT', { childId, id })
                : generateRoute('SAFEGUARDING.MEDICATION_FORM.EDIT', { id })}
              type={TYPES.EDIT}
            />
          )}
          {status !== MEDICATION_FORM_STATUSES.DRAFT && (
            <React.Fragment>
              <DropdownMenu.Item
                icon="detail"
                label={i18n.t('global:ShowDetails')}
                to={childId
                  ? generateRoute('CHILDREN.CHILD.SAFEGUARDING.MEDICATION_FORM.PREVIEW', { childId, id })
                  : generateRoute('SAFEGUARDING.MEDICATION_FORM.PREVIEW', { id })}
              />
              {status !== MEDICATION_FORM_STATUSES.PAST && status !== MEDICATION_FORM_STATUSES.UPCOMING && (
                <DropdownMenu.Item
                  icon="refresh"
                  label={i18n.t('module:Safeguarding:MedicationForm:Preview:ReviewForm:reviewMedicationForm')}
                  to={reviewFormRoute}
                />
              )}
              <DropdownMenu.Item
                icon="export"
                label={i18n.t('global:Export')}
                onClick={() => onExportClick(id)}
              />
            </React.Fragment>
          )}
        </DropdownMenu>
      ),
      child: child ? (
        <Avatar
          avatarSize="small"
          initials={[child.firstName, child.surname]}
          src={child.photo}
          title={`${child.firstName} ${child.surname}`}
        />
      ) : undefined,
      expiryDate: expiryDate
        ? <DateString date={expiryDate as Date} />
        : null,
      expiryDateValue: expiryDate,
      frequency: frequency ? getFrequency(frequency, dose) : null,
      id,
      medication: (
        <Typography width="120px" ellipsis primary>
          <Link to={getRoute({ childId, id, status })}>
            {medication}
          </Link>
        </Typography>
      ),
      reviewDate: nextReviewDate ? <DateString date={nextReviewDate as Date} /> : null,
      startEndDate: startDate && endDate ? (
        <Typography nowrap>
          <DateString.Range endDate={endDate} local={false} startDate={startDate} />
        </Typography>
      ) : null,
      status: MEDICATION_FORM_STATUS_LABELS[status],
      statusValue: status,
    }
  })
}

